import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { UserPet, PetMovement } from '@typings/pets/pet.interface';
import {
  PetPersonalityType,
  PetPersonalityTypeScripts,
} from '../PetPersonality.ts/PetPersonalityType';
import { useDocument } from '@nandorojo/swr-firestore';

// 펫 성격 설정 문서 타입
type PersonalitiesDoc = {
  types: Record<string, string>;
  scripts: Record<string, string[]>;
  updatedAt?: any;
};

interface Props {
  pet: UserPet;
  movement?: PetMovement;
  onClick?: () => void;
  containerBounds?: DOMRect;
}

// 기본 이동 설정
const defaultMovement: PetMovement = {
  speed: 0.05,
  smoothness: 0.8,
  mouseSensitivity: 0.1, // 마우스 민감도 (0.1 = 마우스 10 움직이면 펫 1 움직임)
  boundaries: {
    minX: 50,
    maxX: 400,
    minY: 50,
    maxY: 300,
  },
};

const PetCharacter: React.FC<Props> = ({
  pet,
  movement = defaultMovement,
  onClick,
  containerBounds,
}) => {
  const petRef = useRef<HTMLDivElement>(null);

  // 커스텀 성격/대사 설정 가져오기
  const { data: personalitiesData } = useDocument<PersonalitiesDoc>(
    'petSetting/personalities',
    { listen: true },
  );

  // 기본값 + 커스텀 대사 병합
  const mergedScripts = useMemo(() => {
    const defaultScripts = { ...PetPersonalityTypeScripts } as Record<string, string[]>;
    const customScripts = personalitiesData?.scripts || {};
    // 커스텀이 있으면 커스텀 우선, 없으면 기본값
    const merged: Record<string, string[]> = { ...defaultScripts };
    Object.keys(customScripts).forEach((key) => {
      if (customScripts[key] && customScripts[key].length > 0) {
        merged[key] = customScripts[key];
      }
    });
    return merged;
  }, [personalitiesData]);

  const petSize = 100;
  const bottomPadding = 60;
  const verticalSway = 24; // Y축은 작게 흔들
  const horizontalSway = 60; // X축은 조금 더 넓게

  const clamp = (v: number, min: number, max: number) =>
    Math.max(min, Math.min(max, v));

  // 🎯 컨테이너 하단 기준점 계산
  const getAnchoredBase = useCallback(() => {
    if (containerBounds) {
      const baseY = clamp(
        containerBounds.height - petSize - bottomPadding,
        movement.boundaries.minY,
        Math.max(
          movement.boundaries.minY,
          containerBounds.height - petSize - bottomPadding,
        ),
      );
      const baseX = (containerBounds.width - petSize) / 2;
      return { baseX, baseY };
    }
    // 컨테이너 정보가 없을 때는 기존 boundaries 기준으로 하단 쪽에 고정
    const baseY = Math.max(
      movement.boundaries.minY,
      movement.boundaries.maxY - bottomPadding,
    );
    const baseX =
      (movement.boundaries.minX + movement.boundaries.maxX - petSize) / 2;
    return { baseX, baseY };
  }, [containerBounds, movement.boundaries.minY, movement.boundaries.maxY]);

  const getInitialCenter = useCallback(() => {
    const { baseX, baseY } = getAnchoredBase();
    return { x: baseX, y: baseY };
  }, [getAnchoredBase]);

  const [petImgURL, setPetImgURL] = useState(pet.customImgURL || '');
  const [position, setPosition] = useState(getInitialCenter());
  const [target, setTarget] = useState(getInitialCenter());
  const animationRef = useRef<number>();
  const [isHovered, setIsHovered] = useState(false);
  const [currentDialogue, setCurrentDialogue] = useState<string>('');
  const dialogueTimerRef = useRef<NodeJS.Timeout>();
  const dialogueHideTimerRef = useRef<NodeJS.Timeout>();

  // 컨테이너 변경 시 기준점 재설정
  useEffect(() => {
    const c = getInitialCenter();
    setPosition(c);
    setTarget(c);
  }, [getInitialCenter]);

  // 🖼️ 활성 펫 변경 시 이미지 즉시 동기화
  useEffect(() => {
    setPetImgURL(pet.customImgURL || '');
  }, [pet.id, pet.customImgURL]);

  // 🎯 성격별 랜덤 대사 생성
  const generateRandomDialogue = useCallback(() => {
    if (!pet.personalities || pet.personalities.length === 0) return '';

    // 펫의 성격 중 랜덤으로 하나 선택
    const randompersonalities = pet.personalities[
      Math.floor(Math.random() * pet.personalities.length)
    ] as string;

    // 해당 성격의 대사 배열 가져오기 (커스텀 설정 우선)
    const dialogues = mergedScripts[randompersonalities];
    if (!dialogues || dialogues.length === 0) return '';

    // 대사 중 랜덤으로 하나 선택
    const randomDialogue =
      dialogues[Math.floor(Math.random() * dialogues.length)];
    return randomDialogue;
  }, [pet.personalities, mergedScripts]);

  // 🕐 대사 타이머 설정
  const startDialogueTimer = useCallback(() => {
    const setNextDialogue = () => {
      const newDialogue = generateRandomDialogue();
      setCurrentDialogue(newDialogue);

      // 대사를 5-8초 후에 숨기기
      const hideDelay = Math.floor(Math.random() * 3000) + 5000; // 5000ms ~ 8000ms
      dialogueHideTimerRef.current = setTimeout(() => {
        setCurrentDialogue('');
      }, hideDelay);

      // 1분~2분 사이의 랜덤한 시간 설정 (60000ms ~ 120000ms)
      const randomInterval = Math.floor(Math.random() * 60000) + 60000;

      dialogueTimerRef.current = setTimeout(() => {
        setNextDialogue();
      }, randomInterval);
    };

    // 첫 대사는 5초 후에 설정 (컴포넌트 로드 직후는 너무 급작스러우니까)
    dialogueTimerRef.current = setTimeout(() => {
      setNextDialogue();
    }, 5000);
  }, [generateRandomDialogue]);

  // 🔄 대사 시스템 초기화
  useEffect(() => {
    startDialogueTimer();

    return () => {
      if (dialogueTimerRef.current) {
        clearTimeout(dialogueTimerRef.current);
      }
      if (dialogueHideTimerRef.current) {
        clearTimeout(dialogueHideTimerRef.current);
      }
    };
  }, [startDialogueTimer]);

  // 클릭 시 즉시 랜덤 대사 출력
  const handleClickPet = useCallback(() => {
    if (dialogueHideTimerRef.current)
      clearTimeout(dialogueHideTimerRef.current);
    const newDialogue = generateRandomDialogue();
    setCurrentDialogue(newDialogue);
    const hideDelay = Math.floor(Math.random() * 3000) + 5000;
    dialogueHideTimerRef.current = setTimeout(() => {
      setCurrentDialogue('');
    }, hideDelay);
  }, [generateRandomDialogue]);

  // 🖱️ 마우스 트래킹 (하단 기준으로 작은 sway만 허용)
  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      const { baseX, baseY } = getAnchoredBase();

      if (!containerBounds) {
        const ratioX = event.clientX / (window.innerWidth || 1) - 0.5;
        const ratioY = event.clientY / (window.innerHeight || 1) - 0.5;
        const newTarget = {
          x: clamp(
            baseX + ratioX * horizontalSway,
            movement.boundaries.minX,
            movement.boundaries.maxX,
          ),
          y: clamp(
            baseY + ratioY * verticalSway,
            baseY - verticalSway,
            baseY + verticalSway,
          ),
        };
        setTarget(newTarget);
      } else {
        const rect = containerBounds;
        const ratioX = (event.clientX - rect.left) / rect.width - 0.5;
        const ratioY = (event.clientY - rect.top) / rect.height - 0.5;
        const minX = movement.boundaries.minX;
        const maxX = Math.min(movement.boundaries.maxX, rect.width - petSize);

        const newTarget = {
          x: clamp(baseX + ratioX * horizontalSway, minX, maxX),
          y: clamp(
            baseY + ratioY * verticalSway,
            baseY - verticalSway,
            baseY + verticalSway,
          ),
        };
        setTarget(newTarget);
      }
    },
    [
      containerBounds,
      getAnchoredBase,
      movement.boundaries.minX,
      movement.boundaries.maxX,
    ],
  );

  // 🎯 부드러운 이동 애니메이션
  const animate = useCallback(() => {
    setPosition((prevPosition) => {
      const dx = target.x - prevPosition.x;
      const dy = target.y - prevPosition.y;

      // 거리 기반 속도 조절
      const distance = Math.sqrt(dx * dx + dy * dy);
      const currentSpeed = Math.min(
        movement.speed * distance * 0.1,
        movement.speed * 10,
      );

      return {
        x: prevPosition.x + dx * currentSpeed,
        y: prevPosition.y + dy * currentSpeed,
      };
    });

    animationRef.current = requestAnimationFrame(animate);
  }, [target, movement.speed]);

  // 🔄 이벤트 리스너 및 애니메이션 설정
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [handleMouseMove, animate]);

  return (
    <PetContainer
      ref={petRef}
      style={{
        transform: `translate(${position.x}px, ${position.y}px)`,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => {
        handleClickPet();
        if (onClick) onClick();
      }}
      $isHovered={isHovered}
    >
      <PetImage src={petImgURL} alt={pet.customName} />

      {/* 💬 대사 말풍선 */}
      {currentDialogue && <DialogueBubble>{currentDialogue}</DialogueBubble>}
    </PetContainer>
  );
};

// 🎨 스타일 컴포넌트
const PetContainer = styled.div<{ $isHovered: boolean }>`
  position: absolute;
  width: 100px;
  height: 100px;
  cursor: var(--hoverCursorURL), pointer;
  transition: filter 0.2s ease;
  z-index: 1;

  ${({ $isHovered }) =>
    $isHovered &&
    `
    filter: brightness(1.1) drop-shadow(0 0 10px rgba(255, 255, 255, 0.3));
  `}
`;

const PetImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  user-select: none;
  pointer-events: none;
`;

const DialogueBubble = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, calc(-100% - 8px));
  background: white;
  color: #333;
  padding: 6px 10px;
  border-radius: 12px;
  font-size: 11px;
  width: max-content;
  word-break: keep-all;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border: 1px solid #ddd;
  z-index: 2;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-bottom: 0;
    border-top-color: white;
  }
`;

export default PetCharacter;
